import {
  addListener,
  createListenerMiddleware,
  isAnyOf,
  TypedAddListener,
  TypedStartListening,
} from "@reduxjs/toolkit";
import {
  bindThunk,
  issueCommandThunk,
  abortFleetInCellThunk,
  restartUavConnection,
  setTelemetry,
  setUavNotifications,
  telemetryNotify,
  telemetryAbort,
  telemetryOld,
  telemetryStale,
  tokenExpired,
  tokenExpiring,
  onEvent,
  setPadStatus,
} from "../features/aircraft/aircraftSlice";
import {
  DirectTelemetry,
  isCriticalTelemetry,
  NotificationTypes,
} from "../features/aircraft/types";
import { Websockets } from "../features/aircraft/Websockets";
import {
  generateMissionThunk,
  getRoutesThunk,
} from "../features/mission/missionSlice";
import { showSnackbar } from "../features/session/sessionSlice";
import { Logger } from "../tools/Logger";
import { AppDispatch, RootState } from "./store";
import { selectAircraftConnectionById } from "../features/aircraft/aircraftSelectors";
import {
  getPadStatus,
} from "../features/mission/missionAPI";

const API_TIMEOUT_MS = 10000; // 10 seconds
const TELEMETRY_MISSING_LOG_UPPER_BOUND_MS = 10 * 60 * 1000; // 10 minutes

export const requestStatusNotification = (
  requestStatus: string
): NotificationTypes => {
  if (requestStatus === "fulfilled") return "success";
  if (requestStatus === "rejected") return "error";
  return "info";
};
export const notificationMiddleware = createListenerMiddleware();
export const displayNotificationMiddleware = createListenerMiddleware();
export const telemetryMiddleware = createListenerMiddleware();
export const websocketMiddleware = createListenerMiddleware();

export type AppStartListening = TypedStartListening<RootState, AppDispatch>;
export const thunkLifecycleListener =
  notificationMiddleware.startListening as AppStartListening;
export const notificationListener =
  displayNotificationMiddleware.startListening as AppStartListening;
export const timeListener =
  telemetryMiddleware.startListening as AppStartListening;
export const websocketListener =
  websocketMiddleware.startListening as AppStartListening;

export const addAppListener = addListener as TypedAddListener<
  RootState,
  AppDispatch
>;
let timeout: NodeJS.Timeout | null;

// Store previous aircraft states to detect transitions
const previousAircraftStates: { [uavId: string]: string } = {};
// Track start time of telemetry loss per UA
const telemetryLossStartByUav: { [uavId: string]: number } = {};

// Helper function to get GPS coordinates from telemetry
const getGpsCoordinates = (telemetryPayload: any): { lat?: number; lon?: number } => {
  let lat: number | undefined;
  let lon: number | undefined;
  
  if (!isCriticalTelemetry(telemetryPayload)) {
    // For verbose telemetry, check which FCU is in control
    const fcuInControl = telemetryPayload.fcu_in_control;
    
    if (fcuInControl === 1 && telemetryPayload.fcu1?.gps) {
      lat = telemetryPayload.fcu1.gps.lat ?? undefined;
      lon = telemetryPayload.fcu1.gps.lon ?? undefined;
    } else if (fcuInControl === 2 && telemetryPayload.fcu2?.gps) {
      lat = telemetryPayload.fcu2.gps.lat ?? undefined;
      lon = telemetryPayload.fcu2.gps.lon ?? undefined;
    } else if (fcuInControl === 3 && telemetryPayload.fcu3?.gps) {
      lat = telemetryPayload.fcu3.gps.lat ?? undefined;
      lon = telemetryPayload.fcu3.gps.lon ?? undefined;
    }
  } else {
    // For critical telemetry, coordinates are directly available
    lat = telemetryPayload.lat;
    lon = telemetryPayload.lon;
  }
  
  return { lat, lon };
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`API call timed out after ${timeoutMs}ms`)), timeoutMs)
    )
  ]);
};

const handlePadStatusCheck = async (
  telemetryPayload: any,
  listenerApi: any,
  reason: string
) => {
  const { lat, lon } = getGpsCoordinates(telemetryPayload);
  
  if (lat && lon && typeof lat === "number" && typeof lon === "number") {
    try {
      const response = await withTimeout(getPadStatus(lat, lon), API_TIMEOUT_MS);

      if (response && response.name) {
        listenerApi.dispatch(
          setPadStatus({
            uavId: telemetryPayload.uav_id,
            name: response.name,
            lat,
            lon,
          })
        );
      }

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      Logger.error(
        "PadStatusAPI",
        `${reason} - Failed to get pad status for ${telemetryPayload.uav_id}: ${errorMessage}`,
        error
      );
    }
  }
};

const handleAircraftStateTransition = async (
  telemetryPayload: any,
  listenerApi: any
) => {
  if (telemetryPayload?.uav_id && telemetryPayload?.uav_state) {
    const currentState = telemetryPayload.uav_state;
    const previousState = previousAircraftStates[telemetryPayload.uav_id];
    
    const shouldCheckPadStatus = 
      // Bootup - first time we see this aircraft (no previous state)
      (!previousState) ||
      (currentState === "STANDBY" && previousState !== "STANDBY") ||
      (currentState === "MAINTENANCE" && previousState !== "MAINTENANCE");
    
    if (shouldCheckPadStatus) {
      const reason = !previousState ? "Bootup" : 
                    currentState === "STANDBY" ? "Entered STANDBY" :
                    "Entered MAINTENANCE";
      
      await handlePadStatusCheck(telemetryPayload, listenerApi, reason);
    }

    previousAircraftStates[telemetryPayload.uav_id] = currentState;
  }
};

thunkLifecycleListener({
  matcher: isAnyOf(
    getRoutesThunk.fulfilled,
    generateMissionThunk.fulfilled,
    getRoutesThunk.rejected,
    generateMissionThunk.rejected,
    issueCommandThunk.fulfilled,
    issueCommandThunk.rejected,
    abortFleetInCellThunk.fulfilled,
    abortFleetInCellThunk.rejected
  ),
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(
      showSnackbar({
        type: requestStatusNotification(action.meta.requestStatus),
        message: action.meta.message,
      })
    );
  },
});
websocketListener({
  matcher: isAnyOf(bindThunk.fulfilled),
  effect: async (action, listenerApi) => {
    if (isAnyOf(bindThunk.fulfilled)(action)) {
      Websockets.getInstance().addUavConnection(
        action.payload.sims,
        action.payload?.token,
        action.payload?.uavId,
        action.payload?.port
      );
    }
  },
});

timeListener({
  matcher: isAnyOf(
    restartUavConnection,
    onEvent,
    setUavNotifications
  ),
  effect: async (action, listenerApi) => {
    if (isAnyOf(restartUavConnection)(action)) {
      Websockets.getInstance().disconnectUav(action.payload.uavId);
      delete previousAircraftStates[action.payload.uavId];
    } else if (isAnyOf(onEvent)(action)) {
      const { uavId, event } = action.payload;
      if (event === "MISSION_STARTED") {
        listenerApi.dispatch(
          setUavNotifications({
            uavId,
            notification: {
              type: "MISSION_STARTED",
              action: [],
              level: 2,
              info: true,
              msg: "Mission started",
              timestamp: Date.now(),
            },
          })
        );
      } else if (event === "MISSION_STOPPED") {
        listenerApi.dispatch(
          setUavNotifications({
            uavId,
            notification: {
              type: "MISSION_STOPPED",
              action: [],
              level: 2,
              info: true,
              msg: "Mission stopped",
              timestamp: Date.now(),
            },
          })
        );
      } else if (event === "VALIDATION_FAILED") {
        listenerApi.dispatch(
          setUavNotifications({
            uavId,
            notification: {
              type: "VALIDATION_FAILED",
              action: [],
              level: 3,
              info: true,
              msg: "Mission validation failed",
              timestamp: Date.now(),
            },
          })
        );
      }
    } else if (isAnyOf(setUavNotifications)(action)) {
      if (
        action.payload.notification.type ===
        "FLIGHT_PHASE_TRANSITION_DELIVERY_DESCENDING"
      ) {
        listenerApi.dispatch(
          onEvent({ uavId: action.payload.uavId, event: "DELIVERY_STARTED" })
        );
      }
      if (action.payload.notification.type ===
          "FLIGHT_PHASE_TRANSITION_DELIVERY_CLOSING_DOORS"
      ) {
        listenerApi.dispatch(
          onEvent({ uavId: action.payload.uavId, event: "DOORS_CLOSED" })
        );
      }
      if (action.payload.notification.type === "DELIVERY_PROGRESS_REQ") {
        if (action.payload.notification.msg === "Doors opened") {
          listenerApi.dispatch(
            onEvent({ uavId: action.payload.uavId, event: "DOORS_OPEN" })
          );
        } else if (action.payload.notification.msg === "Doors closed") {
          listenerApi.dispatch(
            onEvent({ uavId: action.payload.uavId, event: "DOORS_CLOSED" })
          );
        } else if (
          action.payload.notification.msg === "Delivery sequence started"
        ) {
          listenerApi.dispatch(
            onEvent({ uavId: action.payload.uavId, event: "DELIVERY_STARTED" })
          );
        }
      }
      if (action.payload.notification.type === "DELIVERY_CMD_WAIT") {
        listenerApi.dispatch(
          onEvent({ uavId: action.payload.uavId, event: "DELIVERY_COMMAND" })
        );
      }
      if (action.payload.notification.type === "DELIVERY_START_DESCENT") {
        listenerApi.dispatch(
          onEvent({ uavId: action.payload.uavId, event: "AT_DROP" })
        );
      }
      if (action.payload.notification.type === "DELIVERY_CMD_WAIT_TIMEOUT") {
        listenerApi.dispatch(
          onEvent({ uavId: action.payload.uavId, event: "DELIVERY_COMMAND_ENDED",
          })
        );
      }
    }
  },
});

const updateAircraftNotifications = (
  action: {
    payload: { telemetry: DirectTelemetry; simIndex: 0 | 1 };
    type: string;
  },
  dispatch: AppDispatch
) => {
  try {
    if (!action.payload.telemetry) return;
    const { payload: telemetryPayload } = action.payload.telemetry;
    const notification = isCriticalTelemetry(telemetryPayload)
      ? telemetryPayload.notification
      : telemetryPayload.notification;
    if (notification && telemetryPayload.uav_id && notification?.msg) {
      dispatch(
        setUavNotifications({
          uavId: telemetryPayload.uav_id,
          notification: {
            ...notification,
            timestamp: Date.now(),
          },
        })
      );
    }
  } catch (err) {
    Logger.error("notification Middleware", err);
  }
};
const getMission = (
  action: {
    payload: { telemetry: DirectTelemetry; simIndex: 0 | 1 };
    type: string;
  },
  dispatch: AppDispatch
) => {
  try {
    if (!action.payload.telemetry) return;
    const { payload: telemetryPayload } = action.payload.telemetry;
    const missionId = isCriticalTelemetry(telemetryPayload)
      ? telemetryPayload.mission_id
      : telemetryPayload.mission_id;
  } catch (err) {
    Logger.error("notification Middleware", err);
  }
};

const returnPing = (
  action: {
    payload: { telemetry: DirectTelemetry; simIndex: 0 | 1 };
    type: string;
  },
  _: AppDispatch,
  getState: () => RootState
) => {
  try {
    if (!action.payload.telemetry) return;
    const {
      payload: telemetryPayload,
      pilot_info: pilotInfo,
      id,
    } = action.payload.telemetry;
    if (!telemetryPayload.uav_id) return;
    const connection = selectAircraftConnectionById(
      getState(),
      telemetryPayload.uav_id
    );
  } catch (err) {
    Logger.error("notification Middleware", err);
  }
};

notificationListener({
  matcher: isAnyOf(
    setTelemetry,
    telemetryNotify,
    telemetryStale,
    telemetryAbort,
    tokenExpiring,
    tokenExpired,
    telemetryOld
  ),

  effect: async (action, listenerApi) => {
    const state = listenerApi.getState();
    const uavId = action.payload.uavId;
    const direct = state.aircraft.direct?.[uavId];
    const uavState = direct?.payload?.uav_state || "";
    const uavSubState = direct?.payload?.uav_sub_state || "";
    const inDelivery =
      (uavSubState?.includes("STATE_FLIGHT_MISSION_DELIVERY") ||
        uavSubState?.includes("OUTBOUND_DESCENDING") ||
        uavSubState?.includes(
          "STATE_FLIGHT_MISSION_OUTBOUND_WAITING_FOR_DELIVERY_CONFIRMATION"
        )) ?? false;
    const inAbort = uavState === "ABORT";
    const inEmergency = uavSubState?.includes("STATE_FLIGHT_EOPS") ?? false;
    const inCriticalPhase = inDelivery || inAbort || inEmergency;
    const inFlight = uavSubState?.includes("STATE_FLIGHT") ?? false;

    if (isAnyOf(telemetryNotify)(action) && inFlight) {
      if (!telemetryLossStartByUav[uavId]) {
        const lastTimestamp = direct?.timestamp || Date.now();
        telemetryLossStartByUav[uavId] = lastTimestamp;
      }
      const elapsed = Date.now() - telemetryLossStartByUav[uavId];
      if (elapsed <= TELEMETRY_MISSING_LOG_UPPER_BOUND_MS) {
        Logger.log("info", "notification Middleware", "Telemetry missing during flight", {
          uavId,
          missionId: direct?.payload?.mission_id,
          elapsedSec: Math.round(elapsed / 1000),
          lostSince: telemetryLossStartByUav[uavId],
        });
      }
    }

    if (isAnyOf(setTelemetry)(action)) {
      if (telemetryLossStartByUav[uavId]) {
        delete telemetryLossStartByUav[uavId];
      }
      updateAircraftNotifications(action, listenerApi.dispatch);
      returnPing(action, listenerApi.dispatch, listenerApi.getState);

      const telemetryPayload = action.payload.telemetry?.payload;
      await handleAircraftStateTransition(telemetryPayload, listenerApi);
    } else if ((isAnyOf(telemetryStale)(action)) && inCriticalPhase) {
      listenerApi.dispatch(
        setUavNotifications({
          uavId: action.payload.uavId,
          notification: {
            type: "TELEMETRY_STALE",
            action: [],
            level: 2,
            msg: "No telemetry for 15 seconds",
            timestamp: Date.now(),
          },
        })
      );
    } else if (isAnyOf(telemetryAbort)(action)) {
      listenerApi.dispatch(
        setUavNotifications({
          uavId: action.payload.uavId,
          notification: {
            type: "TELEMETRY_ABORT",
            action: [],
            level: 3,
            msg: "No telemetry for 45 seconds",
            timestamp: Date.now(),
          },
        })
      );
    } else if (isAnyOf(tokenExpiring)(action)) {
      listenerApi.dispatch(
        setUavNotifications({
          uavId: action.payload.uavId,
          notification: {
            msg: `Token expiring in ${action.payload.expiresInMinutes} minutes.`,
            type: "TOKEN_EXPIRING",
            action: ["DISCONNECT"],
            level: 4,
            timestamp: Date.now(),
          },
        })
      );
    } else if (isAnyOf(tokenExpired)(action)) {
      listenerApi.dispatch(
        setUavNotifications({
          uavId: action.payload.uavId,
          notification: {
            msg: "Your token has expired, you need to rebind.",
            type: "TOKEN_EXPIRED",
            action: ["DISCONNECT"],
            level: 5,
            timestamp: Date.now(),
          },
        })
      );
    } else if (isAnyOf(telemetryOld)(action)) {
      listenerApi.dispatch(
        setUavNotifications({
          uavId: action.payload.uavId,
          notification: {
            msg: "Telemetry is old.",
            type: "TELEMETRY_OLD",
            action: ["DISCONNECT"],
            level: 5,
            timestamp: Date.now(),
          },
        })
      );
    }
  },
});
